import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { filter, map, range, round, sumBy, toUpper, trim } from "lodash";

export const SHARED_KEY = "SHARED";

const LEAVE_CATEGORIES = ["VACATION", "SICK"];

export const DAY_TYPE_STYLES = {
  weekend: { backgroundColor: "#e9ecef", label: "Weekend" },
  holiday: { backgroundColor: "#ffe0b2", label: "Public Holiday" },
  leave: { backgroundColor: "#d9f7be", label: "Leave Day" },
  work: { backgroundColor: undefined, label: "Working Day" },
};

export const formatHours = (hours) => {
  if (!hours) return "";
  return Number.isInteger(hours) ? `${hours}` : `${round(hours, 2)}`;
};

const formatLeaveType = (leaveType) => {
  const name = trim(leaveType || "");
  if (!name) return "Leave";
  return `${name.charAt(0).toUpperCase()}${name.slice(1).toLowerCase()} Leave`;
};

const percentage = (part, whole) =>
  whole ? `${round((part / whole) * 100, 1).toFixed(1)}%` : "0.0%";

const useTimeSheetGrid = (timeSheetDetails = {}) => {
  const [publicHolidayMap, setPublicHolidayMap] = useState({});

  useEffect(() => {
    axios
      .get("/settings/public-holidays")
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        const holidays = {};
        (Array.isArray(list) ? list : []).forEach((holiday) => {
          holidays[dayjs(holiday.date).format("YYYY-MM-DD")] = holiday.name;
        });
        setPublicHolidayMap(holidays);
      })
      .catch(() => {});
  }, []);

  return useMemo(() => {
    const year = timeSheetDetails?.year;
    const rawMonth = timeSheetDetails?.month;
    const monthDate =
      year && rawMonth
        ? dayjs(`${year}-${String(rawMonth).padStart(2, "0")}-01`)
        : dayjs().startOf("month");

    const activities = timeSheetDetails?.timeSheetActivities || [];
    const getColumnKey = (activity) =>
      activity.project_id ? trim(activity.project_id) : SHARED_KEY;

    const projectColumns = map(timeSheetDetails?.myProjects, (project) => ({
      key: trim(project.id),
      title: toUpper(project?.name || ""),
    }));
    activities.forEach((activity) => {
      const key = getColumnKey(activity);
      if (key !== SHARED_KEY && !projectColumns.some((c) => c.key === key)) {
        projectColumns.push({
          key,
          title: toUpper(activity.project?.name || "OTHER PROJECT"),
        });
      }
    });
    const columns = [...projectColumns, { key: SHARED_KEY, title: "SHARED" }];
    const columnTitles = Object.fromEntries(
      columns.map((column) => [column.key, column.title])
    );

    const approvedLeave = {};
    (timeSheetDetails?.leaveDays || []).forEach((leaveDay) => {
      approvedLeave[leaveDay.date] = leaveDay.leave_type;
    });

    const getActivityDay = (activity) =>
      activity.day_of_month ?? dayjs(activity.date).date();

    const rows = map(range(1, monthDate.daysInMonth() + 1), (day) => {
      const date = monthDate.date(day);
      const dateKey = date.format("YYYY-MM-DD");
      const dayActivities = filter(
        activities,
        (activity) => getActivityDay(activity) === day
      );

      const hoursByColumn = {};
      dayActivities.forEach((activity) => {
        const key = getColumnKey(activity);
        hoursByColumn[key] = (hoursByColumn[key] || 0) + (activity.hours || 0);
      });

      const holidayName = publicHolidayMap[dateKey];
      const approvedLeaveType = approvedLeave[dateKey];
      const isLeave =
        dateKey in approvedLeave ||
        dayActivities.some((activity) =>
          LEAVE_CATEGORIES.includes(toUpper(activity.category || ""))
        );
      const isWeekend = [0, 6].includes(date.day());

      let type = "work";
      if (holidayName) type = "holiday";
      else if (isLeave) type = "leave";
      else if (isWeekend) type = "weekend";

      return {
        key: dateKey,
        day,
        date: date.format("DD/MM/YYYY"),
        dayName: date.format("ddd"),
        holidayName,
        leaveName: isLeave ? formatLeaveType(approvedLeaveType) : "",
        type,
        activities: map(dayActivities, (activity) => ({
          id: activity.id,
          text: trim(activity.activity || ""),
          columnKey: getColumnKey(activity),
          columnTitle: columnTitles[getColumnKey(activity)],
          hours: activity.hours || 0,
        })),
        hoursByColumn,
        total: sumBy(dayActivities, (activity) => activity.hours || 0),
      };
    });

    const totalHours = sumBy(activities, (activity) => activity.hours || 0);
    const totalsByColumn = {};
    const percentagesByColumn = {};
    columns.forEach(({ key }) => {
      const hours = sumBy(
        filter(activities, (activity) => getColumnKey(activity) === key),
        (activity) => activity.hours || 0
      );
      totalsByColumn[key] = hours;
      percentagesByColumn[key] = hours ? percentage(hours, totalHours) : "";
    });

    const countOf = (type) => filter(rows, { type }).length;

    return {
      monthDate,
      monthName: monthDate.format("MMMM"),
      year: monthDate.year(),
      period: `${monthDate.startOf("month").format("DD")}-${monthDate
        .endOf("month")
        .format("DD")} ${monthDate.format("MMMM YYYY")}`,
      columns,
      rows,
      totalHours,
      totalsByColumn,
      percentagesByColumn,
      workedDays: filter(rows, (row) => row.total > 0).length,
      counts: {
        weekend: countOf("weekend"),
        holiday: countOf("holiday"),
        leave: countOf("leave"),
      },
    };
  }, [timeSheetDetails, publicHolidayMap]);
};

export default useTimeSheetGrid;
